import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  FileText,
  Receipt,
  DollarSign,
  ShieldAlert,
  Sliders,
  Settings,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  User,
  Lock,
  LogOut,
  Moon,
  Sun,
  Bell,
  Search,
  Sparkles,
  Megaphone,
  Wifi,
  Webhook,
  Activity
} from "lucide-react";
import { useAuth } from "../../context/Auth/AuthContext";
import { useThemeMode } from "../../context/ThemeMode/ThemeModeContext";

const menuGroups = [
  {
    title: "Visão Geral",
    items: [
      { text: "Dashboard", path: "/dashboard", icon: LayoutDashboard }
    ]
  },
  {
    title: "Clientes & Acessos",
    items: [
      { text: "Empresas", path: "/companies", icon: Building2 },
      { text: "Usuários", path: "/users", icon: Users }
    ]
  },
  {
    title: "Comercial & Financeiro",
    items: [
      { text: "Planos", path: "/plans", icon: CreditCard },
      { text: "Assinaturas", path: "/subscriptions", icon: FileText },
      { text: "Faturas", path: "/invoices", icon: Receipt },
      { text: "Pagamentos", path: "/payments", icon: DollarSign },
      { text: "Integração Asaas", path: "/integrations/asaas", icon: SlidersHorizontal },
      { text: "Configuração Fiscal", path: "/fiscal", icon: FileText },
      { text: "Eventos de Cobrança", path: "/billing-events", icon: DollarSign, pending: true }
    ]
  },
  {
    title: "Controle & Segurança",
    items: [
      { text: "Auditoria", path: "/audit", icon: ShieldAlert },
      { text: "Feature Flags", path: "/feature-flags", icon: Sliders },
      { text: "Configurações Globais", path: "/global-settings", icon: Settings },
      { text: "Configurações", path: "/settings", icon: Settings }
    ]
  },
  {
    title: "Comercial e Cobrança",
    items: [
      { text: "Financeiro", path: "/finance", icon: DollarSign }
    ]
  },
  {
    title: "Comunicação",
    items: [
      { text: "Informativos", path: "/announcements", icon: Megaphone }
    ]
  },
  {
    title: "Operação",
    items: [
      { text: "Gerenciar Conexões", path: "/connections", icon: Wifi },
      { text: "Presets de Integração", path: "/api-presets", icon: Webhook }
    ]
  },
  {
    title: "Sistema",
    items: [
      { text: "Monitoramento", path: "/monitoring", icon: Activity }
    ]
  }
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  const handleNavigate = (path) => {
    history.push(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    setUserDropdownOpen(false);
    await signOut();
    history.push("/login");
  };

  const resolveProfileImage = (u) => {
    if (!u?.profileImage) return null;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base.replace(/\/$/, "")}/public/company${u.companyId}/user/${u.profileImage}`;
  };

  return (
    <div className="min-h-screen bg-[#f4f6f9] dark:bg-[#191c1e] text-gray-900 dark:text-[#eff1f4] flex flex-col font-sans transition-colors duration-300">
      {/* Glass Fixed Top Header */}
      <header className="glass-header h-20 fixed top-0 left-0 right-0 z-40 px-4 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:flex p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            title={sidebarOpen ? "Recolher menu" : "Expandir menu"}
          >
            {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <div
            onClick={() => handleNavigate("/dashboard")}
            className="cursor-pointer flex items-center gap-2"
          >
            <div className="w-9 h-9 rounded-xl create-event-btn flex items-center justify-center font-bold text-white shadow-purple-glow">
              TW
            </div>
            <span className="text-xl sm:text-2xl ticket-whats-logo hidden sm:inline-block">
              TicketWhats
            </span>
          </div>
        </div>

        {/* Header Right Actions */}
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden lg:flex items-center bg-black/5 dark:bg-white/5 border border-gray-200 dark:border-white/5 rounded-full px-3.5 py-1.5 text-xs text-gray-600 dark:text-gray-400 focus-within:border-purple-500/50">
            <Search size={14} className="mr-2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar no admin..."
              className="bg-transparent border-none outline-none text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 w-40"
            />
          </div>

          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-full text-gray-600 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-300 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            title={mode === "dark" ? "Alternar para Modo Claro" : "Alternar para Modo Escuro"}
          >
            {mode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button className="p-2.5 rounded-full text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-black/5 dark:hover:bg-white/5 relative">
            <Bell size={18} />
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
          </button>

          {/* User Profile Dropdown Trigger */}
          <div className="relative">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5 border border-transparent hover:border-gray-200 dark:hover:border-white/10 transition-all"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden border border-purple-500/30 bg-purple-600/20 text-purple-700 dark:text-white flex items-center justify-center font-bold text-sm">
                {resolveProfileImage(user) ? (
                  <img src={resolveProfileImage(user)} alt="User Profile" className="w-full h-full object-cover" />
                ) : (
                  user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "A"
                )}
              </div>
              <div className="hidden md:block text-left text-xs">
                <div className="font-bold text-gray-800 dark:text-gray-200 truncate max-w-[120px]">
                  {user?.name || user?.email || "Admin"}
                </div>
                <div className="text-[10px] text-purple-600 dark:text-purple-400 font-semibold uppercase tracking-wider">
                  Superadmin
                </div>
              </div>
            </button>

            {/* Dropdown Menu */}
            {userDropdownOpen && (
              <div className="absolute right-0 mt-2 w-52 neumorphic-card rounded-2xl p-1.5 z-50 shadow-2xl border border-gray-200 dark:border-white/10">
                <div className="px-3 py-2 border-b border-gray-100 dark:border-white/5 mb-1">
                  <div className="text-xs font-bold text-gray-900 dark:text-gray-200">{user?.name || "Admin"}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
                </div>
                <button
                  onClick={() => { setUserDropdownOpen(false); handleNavigate("/profile"); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-white hover:bg-purple-50 dark:hover:bg-white/5 transition-colors"
                >
                  <User size={15} className="text-purple-500 dark:text-purple-400" /> Perfil
                </button>
                <button
                  onClick={() => { setUserDropdownOpen(false); handleNavigate("/change-password"); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-white hover:bg-purple-50 dark:hover:bg-white/5 transition-colors"
                >
                  <Lock size={15} className="text-purple-500 dark:text-purple-400" /> Alterar Senha
                </button>
                <div className="my-1 border-t border-gray-100 dark:border-white/5" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                >
                  <LogOut size={15} /> Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Layout Content & Sidebar Wrapper */}
      <div className="flex flex-1 pt-20">
        {/* Desktop Sidebar */}
        <aside
          className={`glass-sidebar fixed left-0 top-20 bottom-0 z-30 flex flex-col transition-all duration-300 ${
            sidebarOpen ? "w-64" : "w-20"
          } hidden md:flex`}
        >
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
            {menuGroups.map((group, idx) => (
              <div key={idx}>
                {sidebarOpen && (
                  <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-wider uppercase px-3 mb-2">
                    {group.title}
                  </div>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const active =
                      location.pathname === item.path ||
                      (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigate(item.path)}
                        className={`w-full flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all group ${
                          active
                            ? "active-nav-item"
                            : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-black/5 dark:hover:bg-white/5"
                        }`}
                        title={!sidebarOpen ? item.text : ""}
                      >
                        <IconComponent
                          size={18}
                          className={active ? "text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-300"}
                        />
                        {sidebarOpen && (
                          <span className="truncate flex-1 text-left">{item.text}</span>
                        )}
                        {sidebarOpen && item.pending && (
                          <span className="text-[9px] font-bold bg-amber-500/10 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full uppercase">
                            Pendente
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {sidebarOpen && (
            <div className="p-4 border-t border-gray-200 dark:border-white/5">
              <div className="create-event-btn rounded-2xl p-4 text-white">
                <div className="flex items-center gap-2 font-bold text-xs">
                  <Sparkles size={16} /> Painel SaaS PRO
                </div>
                <p className="text-[11px] text-white/80 mt-1">
                  TicketWhats Admin v1.0.0
                </p>
              </div>
            </div>
          )}
        </aside>

        {/* Mobile Slideover Drawer */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex">
            <div
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />
            <div className="relative w-72 max-w-[80vw] bg-white dark:bg-[#191c1e] border-r border-gray-200 dark:border-white/10 h-full flex flex-col p-4 z-50">
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-white/5 mb-4">
                <span className="text-lg font-bold text-gray-900 dark:text-white">Menu Navegação</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-6">
                {menuGroups.map((group, idx) => (
                  <div key={idx}>
                    <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-wider uppercase px-2 mb-2">
                      {group.title}
                    </div>
                    <div className="space-y-1">
                      {group.items.map((item) => {
                        const active =
                          location.pathname === item.path ||
                          (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
                        const IconComponent = item.icon;
                        return (
                          <button
                            key={item.path}
                            onClick={() => handleNavigate(item.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold ${
                              active
                                ? "active-nav-item"
                                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5"
                            }`}
                          >
                            <IconComponent size={18} />
                            <span>{item.text}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main
          className={`flex-1 transition-all duration-300 p-2.5 sm:p-3.5 min-w-0 ${
            sidebarOpen ? "md:ml-64" : "md:ml-20"
          }`}
        >
          <div className="w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
